import { router, useForm, usePage } from "@inertiajs/react"
import {
  AlertTriangleIcon,
  GlobeIcon,
  InfoIcon,
  RepeatIcon,
  SendIcon,
} from "lucide-react"
import { useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import type { EventRow, PoiOption } from "@/lib/types"
import type { SharedProps } from "@/types"

interface Props {
  event?: EventRow
  pois: PoiOption[]
  mode: "private" | "submit_public"
  createUrl: string
  submitPublicUrl: string
  onClose: () => void
}

interface FormData {
  name: string
  description: string
  starts_at: string
  ends_at: string
  timezone: string
  poi_id: string
  external_url: string
  recurrence_frequency: string
  photos: string[]
}

const TIMEZONES = [
  { value: "UTC", label: "UTC" },
  { value: "Europe/Lisbon", label: "Lisbon (WET)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET)" },
  { value: "Europe/Berlin", label: "Berlin (CET)" },
  { value: "America/New_York", label: "New York (ET)" },
  { value: "America/Chicago", label: "Chicago (CT)" },
  { value: "America/Denver", label: "Denver (MT)" },
  { value: "America/Los_Angeles", label: "Los Angeles (PT)" },
  { value: "America/Sao_Paulo", label: "Sao Paulo (BRT)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Australia/Sydney", label: "Sydney (AEST)" },
]

const RECURRENCE_OPTIONS = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Repeats daily" },
  { value: "weekly", label: "Repeats weekly" },
  { value: "monthly", label: "Repeats monthly" },
]

export function EventForm({ event, pois, mode, createUrl, submitPublicUrl, onClose }: Props) {
  const { errors } = usePage<SharedProps>().props
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)

  const isEditing = Boolean(event)
  const isPublicSubmission = mode === "submit_public"
  const isCancelled = Boolean(event?.cancelled_at)

  const { data, setData, post, patch, processing, transform } = useForm<FormData>({
    name:                 event?.name ?? "",
    description:          event?.description ?? "",
    starts_at:            event?.starts_at ? event.starts_at.slice(0, 16) : "",
    ends_at:              event?.ends_at ? event.ends_at.slice(0, 16) : "",
    timezone:             event?.timezone ?? "Europe/Lisbon",
    poi_id:               event?.poi_id != null ? String(event.poi_id) : "",
    external_url:         event?.external_url ?? "",
    recurrence_frequency: event?.recurrence?.frequency ?? "",
    photos:               event?.photos ?? [],
  })

  transform((d) => ({
    event: {
      name:         d.name,
      description:  d.description,
      starts_at:    d.starts_at,
      ends_at:      d.ends_at || null,
      timezone:     d.timezone,
      poi_id:       d.poi_id || null,
      external_url: d.external_url || null,
      recurrence:   d.recurrence_frequency ? { frequency: d.recurrence_frequency } : null,
      photos:       d.photos,
    },
  }))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isPublicSubmission) {
      post(submitPublicUrl, { onSuccess: onClose })
      return
    }
    if (event) {
      patch(event.update_url!, { onSuccess: onClose })
    } else {
      post(createUrl, { onSuccess: onClose })
    }
  }

  function handleCancelEvent() {
    if (!event) return
    router.patch(
      event.cancel_url!,
      {},
      { onSuccess: onClose },
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-6">
        {/* Mode banner for public submissions */}
        {isPublicSubmission && (
          <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
            <GlobeIcon className="mt-0.5 size-4 shrink-0" />
            <div>
              <p className="font-medium">Submitting for moderation</p>
              <p className="mt-0.5 text-xs opacity-80">
                This event will be reviewed before it becomes visible to all properties in your region.
              </p>
            </div>
          </div>
        )}

        {/* Cancelled banner */}
        {isCancelled && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            <AlertTriangleIcon className="size-4 shrink-0" />
            This event has been cancelled.
          </div>
        )}

        {/* --- Event details section --- */}
        <div className="grid gap-1.5">
          <Label htmlFor="event-name">
            Event name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="event-name"
            value={data.name}
            onChange={(e) => setData("name", e.target.value)}
            placeholder="e.g. Live Fado Night, Rooftop Yoga"
            required
          />
          {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="event-description">Description</Label>
          <Textarea
            id="event-description"
            value={data.description}
            onChange={(e) => setData("description", e.target.value)}
            rows={3}
            placeholder="What can guests expect?"
          />
          {errors.description && (
            <p className="text-xs text-destructive">{errors.description}</p>
          )}
        </div>

        {/* --- Date & time section --- */}
        <Separator />
        <fieldset className="grid gap-4">
          <legend className="text-sm font-medium">Date and time</legend>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="event-starts-at">
                Starts <span className="text-destructive">*</span>
              </Label>
              <Input
                id="event-starts-at"
                type="datetime-local"
                value={data.starts_at}
                onChange={(e) => setData("starts_at", e.target.value)}
                required
              />
              {errors.starts_at && (
                <p className="text-xs text-destructive">{errors.starts_at}</p>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="event-ends-at">Ends</Label>
              <Input
                id="event-ends-at"
                type="datetime-local"
                value={data.ends_at}
                onChange={(e) => setData("ends_at", e.target.value)}
                min={data.starts_at || undefined}
              />
              {errors.ends_at && (
                <p className="text-xs text-destructive">{errors.ends_at}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="event-timezone">Timezone</Label>
              <Select
                value={data.timezone}
                onValueChange={(v) => setData("timezone", v)}
              >
                <SelectTrigger id="event-timezone">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.timezone && (
                <p className="text-xs text-destructive">{errors.timezone}</p>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="event-recurrence">
                <RepeatIcon className="mr-1 inline size-3.5" />
                Repeats
              </Label>
              <Select
                value={data.recurrence_frequency || "none"}
                onValueChange={(v) =>
                  setData("recurrence_frequency", v === "none" ? "" : v)
                }
              >
                <SelectTrigger id="event-recurrence">
                  <SelectValue placeholder="Does not repeat" />
                </SelectTrigger>
                <SelectContent>
                  {RECURRENCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </fieldset>

        {/* --- Links & associations --- */}
        <Separator />
        <fieldset className="grid gap-4">
          <legend className="text-sm font-medium">Links</legend>

          {pois.length > 0 && (
            <div className="grid gap-1.5">
              <Label htmlFor="event-poi">
                Location (POI)
                <span className="ml-1 font-normal text-muted-foreground">
                  -- optional
                </span>
              </Label>
              <Select
                value={data.poi_id || "none"}
                onValueChange={(v) => setData("poi_id", v === "none" ? "" : v)}
              >
                <SelectTrigger id="event-poi">
                  <SelectValue placeholder="No linked location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked location</SelectItem>
                  {pois.map((poi) => (
                    <SelectItem key={poi.id} value={String(poi.id)}>
                      {poi.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Link this event to a place of interest so guests can find it on the map.
              </p>
              {errors.poi_id && (
                <p className="text-xs text-destructive">{errors.poi_id}</p>
              )}
            </div>
          )}

          <div className="grid gap-1.5">
            <Label htmlFor="event-url">External URL</Label>
            <Input
              id="event-url"
              type="url"
              value={data.external_url}
              onChange={(e) => setData("external_url", e.target.value)}
              placeholder="https://tickets.example.com"
            />
            <p className="text-xs text-muted-foreground">
              Ticket page, booking link, or event website.
            </p>
            {errors.external_url && (
              <p className="text-xs text-destructive">{errors.external_url}</p>
            )}
          </div>
        </fieldset>

        {/* --- Form actions --- */}
        <Separator />
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? (
              isPublicSubmission ? "Submitting..." : "Saving..."
            ) : isPublicSubmission ? (
              <>
                <SendIcon className="mr-1.5 size-4" />
                Submit for review
              </>
            ) : isEditing ? (
              "Save changes"
            ) : (
              "Create event"
            )}
          </Button>
        </div>

        {/* --- Danger zone: Cancel Event (only for existing, non-cancelled events) --- */}
        {isEditing && !isCancelled && (
          <>
            <Separator />
            <div className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <div className="flex items-start gap-2">
                <InfoIcon className="mt-0.5 size-4 text-destructive" />
                <div>
                  <p className="text-sm font-medium text-destructive">Cancel this event</p>
                  <p className="text-xs text-muted-foreground">
                    This will hide the event from guest guides. It cannot be undone.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setCancelDialogOpen(true)}
              >
                Cancel event
              </Button>
            </div>
          </>
        )}
      </form>

      {/* Cancel event confirmation dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this event?</AlertDialogTitle>
            <AlertDialogDescription>
              The event will be marked as cancelled and removed from guest guides. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleCancelEvent}>
              Cancel event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

